import ReactGA from "react-ga4";
import React from "react";

// Initialize Google Analytics
export const initGA = (trackingId) => {
  // Check if trackingId is valid, not a placeholder, and is a GA4 ID
  if (
    !trackingId ||
    trackingId === "your-tracking-id-here" || // Placeholder
    trackingId === "UA-XXXXXXXXX-X" || // Old UA placeholder
    !trackingId.startsWith("G-") // GA4 IDs start with G-
  ) {
    let warningMessage =
      "Google Analytics tracking ID is missing or invalid. Analytics will not be initialized.";
    if (trackingId && trackingId.startsWith("UA-")) {
      warningMessage =
        "Looks like you're using a Universal Analytics ID (UA-XXXXXXXXX-X). Please use a Google Analytics 4 Measurement ID (G-XXXXXXXXXX).";
    } else if (
      trackingId &&
      !trackingId.startsWith("G-") &&
      trackingId !== "your-tracking-id-here"
    ) {
      warningMessage = `Invalid Google Analytics 4 Measurement ID format: ${trackingId}. It should start with 'G-'. Analytics will not be initialized.`;
    }
    console.warn(warningMessage);
    return;
  }

  if (!window.GA_INITIALIZED) {
    // ensure trackingId is implicitly checked by the conditions above
    ReactGA.initialize(trackingId);
    window.GA_INITIALIZED = true;
  }
};

// Track page views - ReactGA4 handles this automatically when initialized.
// This function is no longer needed and should be removed from where it's called.
// export const trackPageView = (path) => {
//   const page = path || window.location.pathname + window.location.search;
//   console.log(`Automatic page view tracking enabled by GA4 for: ${page}`);
// };

// Track events
export const trackEvent = (category, action, label, value) => {
  // GA4 event structure is flexible. This maps UA-style events.
  // For more GA4-centric tracking, consider using recommended event names and parameters.
  ReactGA.event({
    category,
    action,
    label,
    value, // value should be a number
  });
};

// Track timing (as a custom event in GA4)
export const trackTiming = (category, variable, value, label) => {
  ReactGA.event({
    category: category, // Or a more generic GA4 category like 'engagement'
    action: variable, // The timing variable
    label: label, // Optional label for context
    value: parseInt(value, 10), // Ensure value is an integer for GA4
  });
};

// Track user characteristics (demographics)
export const setUserProperties = (properties) => {
  ReactGA.set(properties); // GA4 uses 'set' for user properties
};

// Track exceptions (as a custom event in GA4)
export const trackException = (description, fatal = false) => {
  ReactGA.event({
    category: "Exception",
    action: description,
    label: fatal ? "Fatal" : "Non-fatal",
  });
};

// Track social interactions (as a custom event in GA4)
export const trackSocial = (network, action, target) => {
  ReactGA.event({
    category: "SocialInteraction", // Or a more generic GA4 category
    action: action,
    label: `${network} - ${target}`, // Combine network and target into label
  });
};

// Helper for outbound link tracking (as a custom event in GA4)
export const trackOutboundLink = (
  url,
  category = "Outbound Link",
  callback
) => {
  ReactGA.event({
    category: category,
    action: "Click",
    label: url,
  });
  if (callback && typeof callback === "function") {
    // GA's hitCallback is tricky. For simplicity, just call the callback.
    // If navigation needs to be delayed, that logic should be handled by the caller
    // or by using navigator.sendBeacon if appropriate.
    callback();
  }
};

// Custom hooks and utilities for component tracking

// Hook to track component mount time
export const useTrackComponentLoadTime = (componentName) => {
  const startTime = Date.now();

  return () => {
    const endTime = Date.now();
    const loadTime = endTime - startTime;

    trackTiming(
      "Component Load Time",
      componentName,
      loadTime,
      "Component Mount to Render Complete"
    );

    return loadTime;
  };
};

// Hook for tracking visibility/scroll
export const useTrackElementVisibility = (elementRef, options = {}) => {
  const {
    rootMargin = "0px",
    threshold = 0.1,
    once = true,
    category = "Element Visibility",
    action = "Element Viewed",
    label,
  } = options;

  const alreadyTracked = React.useRef(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            (!once || (once && !alreadyTracked.current))
          ) {
            trackEvent(category, action, label || "Element entered viewport");
            alreadyTracked.current = true;

            if (once) {
              observer.disconnect();
            }
          }
        });
      },
      { rootMargin, threshold }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      // Check if elementRef.current exists before trying to disconnect
      // It might have been unmounted and ref might be null
      if (elementRef.current && observer) {
        // also check observer instance
        observer.disconnect();
      }
    };
  }, [elementRef, rootMargin, threshold, once, category, action, label]);
};

// Track form interactions
export const trackFormInteraction = (formName, action, fieldName = null) => {
  trackEvent(
    "Form Interaction",
    action,
    fieldName ? `${formName} - ${fieldName}` : formName
  );
};

// Track search queries
export const trackSearch = (
  searchTerm,
  category = "Search",
  resultsCount = null // GA4 'value' should be a number.
) => {
  trackEvent(
    category,
    "Search",
    searchTerm,
    resultsCount !== null ? Number(resultsCount) : undefined
  );
};

// Track page/component load performance
export const trackPerformance = () => {
  // Check if browser supports performance API
  if (typeof window.performance !== "undefined" && window.performance.timing) {
    // Wait for page to fully load to calculate metrics
    window.addEventListener("load", () => {
      setTimeout(() => {
        // Use setTimeout to ensure all metrics are available
        const timing = window.performance.timing;

        // Calculate key performance metrics
        const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
        const domReadyTime = timing.domComplete - timing.domLoading;
        const networkLatency = timing.responseEnd - timing.requestStart; // Often referred to as TTFB
        const serverResponseTime = timing.responseEnd - timing.requestStart; // Same as networkLatency here
        const redirectTime = timing.redirectEnd - timing.redirectStart || 0;

        if (pageLoadTime > 0) {
          trackTiming(
            "Page Performance",
            "Total Load Time",
            pageLoadTime,
            window.location.pathname
          );
        }

        if (domReadyTime > 0) {
          trackTiming(
            "Page Performance",
            "DOM Processing",
            domReadyTime,
            window.location.pathname
          );
        }

        if (networkLatency > 0) {
          trackTiming(
            "Page Performance",
            "Network Latency (TTFB)",
            networkLatency,
            window.location.pathname
          );
        }

        // serverResponseTime is usually the same as networkLatency (TTFB)
        // trackTiming(
        //   "Page Performance",
        //   "Server Response",
        //   serverResponseTime,
        //   window.location.pathname
        // );

        if (redirectTime > 0) {
          trackTiming(
            "Page Performance",
            "Redirect Time",
            redirectTime,
            window.location.pathname
          );
        }

        // Track Navigation API metrics if available (more modern approach)
        if (window.performance && window.performance.getEntriesByType) {
          const navigationEntries =
            window.performance.getEntriesByType("navigation");
          if (navigationEntries.length > 0) {
            const navData = navigationEntries[0]; // PerformanceNavigationTiming

            if (navData.domContentLoadedEventEnd > 0) {
              trackTiming(
                "Page Performance",
                "DOM Content Loaded",
                navData.domContentLoadedEventEnd,
                window.location.pathname
              );
            }

            // Time to First Byte (TTFB) - more accurate from Navigation Timing API
            if (navData.responseStart > 0) {
              trackTiming(
                "Page Performance",
                "Time to First Byte",
                navData.responseStart, // This is a timestamp, duration is navData.responseStart - navData.startTime
                window.location.pathname
              );
            }

            // First Contentful Paint (FCP) - if available through PerformancePaintTiming
            const paintEntries = window.performance.getEntriesByType("paint");
            const fcpEntry = paintEntries.find(
              (entry) => entry.name === "first-contentful-paint"
            );
            if (fcpEntry) {
              trackTiming(
                "Page Performance",
                "First Contentful Paint (FCP)",
                fcpEntry.startTime, // This is a timestamp, duration from navigationStart
                window.location.pathname
              );
            }
          }
        }
      }, 0); // setTimeout with 0ms to run after current execution stack
    });
  }
};

// Make sure all previously exported functions are still exported
// trackPageView is intentionally removed.
export default {
  initGA,
  // trackPageView, // Removed
  trackEvent,
  trackTiming,
  setUserProperties,
  trackException,
  trackSocial,
  trackOutboundLink,
  useTrackComponentLoadTime,
  useTrackElementVisibility,
  trackFormInteraction,
  trackSearch,
  trackPerformance,
};
